import Swal from "sweetalert2";

let lastPopup = { message: "", timestamp: 0 };

/**
 * Sanitizes input text, removing HTML tags and extracting embedded JSON error messages if present.
 */
export const sanitizeMessage = (rawMessage) => {
  if (!rawMessage) return "An unexpected error occurred. Please try again.";

  let msgStr = typeof rawMessage === "object"
    ? rawMessage.error || rawMessage.message || JSON.stringify(rawMessage)
    : String(rawMessage);

  // Check if string contains JSON embedded inside or after HTML string
  const jsonMatch = msgStr.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed.error || parsed.message) {
        msgStr = parsed.error || parsed.message;
      }
    } catch (e) {
      // Ignore parse failure, fall back to string cleaning
    }
  }

  // Strip HTML tags (e.g. <br/><b>Warning</b>: ...)
  let cleanMsg = msgStr.replace(/<[^>]*>/g, "").trim();

  // Normalize multiple spaces/newlines
  cleanMsg = cleanMsg.replace(/\s+/g, " ");

  if (!cleanMsg) {
    return "An unexpected error occurred. Please try again.";
  }

  return cleanMsg;
};

/**
 * Checks if a popup with the exact same message was shown recently (within 1.5s).
 */
const isDuplicate = (cleanMessage) => {
  const now = Date.now();
  if (lastPopup.message === cleanMessage && now - lastPopup.timestamp < 1500) {
    return true;
  }
  lastPopup = { message: cleanMessage, timestamp: now };
  return false;
};

/**
 * Custom SweetAlert mixin with standard professional styling and auto-timer.
 */
const createSwalPopup = (type, title, message, timerMs) => {
  const cleanMsg = sanitizeMessage(message);

  if (isDuplicate(cleanMsg)) {
    return Promise.resolve();
  }

  return Swal.fire({
    icon: type,
    title: title || (type.charAt(0).toUpperCase() + type.slice(1)),
    text: cleanMsg,
    timer: timerMs,
    timerProgressBar: true,
    showConfirmButton: true,
    confirmButtonColor: type === "error" ? "#d33" : type === "warning" ? "#f39c12" : "#3085d6",
    customClass: {
      popup: "swal2-professional-popup",
      title: "swal2-professional-title",
      htmlContainer: "swal2-professional-content"
    }
  });
};

/**
 * Success Popup - Auto-closes in 3 seconds (3000 ms)
 */
export const showSuccess = (message, title = "Success") => {
  return createSwalPopup("success", title, message, 3000);
};

/**
 * Info Popup - Auto-closes in 3 seconds (3000 ms)
 */
export const showInfo = (message, title = "Information") => {
  return createSwalPopup("info", title, message, 3000);
};

/**
 * Warning Popup - Auto-closes in 4 seconds (4000 ms)
 */
export const showWarning = (message, title = "Warning") => {
  return createSwalPopup("warning", title, message, 4000);
};

/**
 * Error Popup - Auto-closes in 4 seconds (4000 ms)
 */
export const showError = (message, title = "Error") => {
  return createSwalPopup("error", title, message, 4000);
};

/**
 * Confirm Dialog Modal for Destructive or Important Actions
 */
export const showConfirm = ({
  title = "Are you sure?",
  text = "You won't be able to revert this!",
  confirmButtonText = "Yes, proceed",
  cancelButtonText = "Cancel",
  icon = "warning"
} = {}) => {
  return Swal.fire({
    title,
    text: sanitizeMessage(text),
    icon,
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText,
    cancelButtonText
  });
};

export default {
  showSuccess,
  showInfo,
  showWarning,
  showError,
  showConfirm,
  sanitizeMessage
};
